import { DEPARTMENT_IDS } from "@wallyo/contracts/workspaces";
import { workspaceLabel } from "../../navigation/screens";
import {
  SET_PASSWORD_CODE_TTL_HOURS,
  type GetStaffMeResponse,
  type StaffMemberListItem,
  type StaffRoleListItem,
} from "@wallyo/contracts/console";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useApi } from "../../api/context";
import { createIntent } from "../../api/idempotency";
import { t } from "../../i18n";
import { heldPermission, permissionRows, rolePermission, type GridFeature, type GridPermission } from "./team-access";
import {
  countLabel,
  describeWriteFailure,
  featureName,
  formatDateTime,
  permissionName,
  type WriteFailure,
} from "./team-format";
import {
  inviteProblem,
  reasonProblem,
  roleFieldsProblem,
  sendCreateRole,
  sendDeactivate,
  sendReactivate,
  sendEditRole,
  sendEndSessions,
  currencySuggestions,
  defaultCurrency,
  limitRowsOf,
  limitsOfRows,
  limitsProblem,
  sendFeatureChange,
  sendInvite,
  sendMembership,
  sendPermissionChange,
  type InviteFields,
  type LimitRow,
  type RoleFields,
} from "./team-writes";

// ── Building blocks ──────────────────────────────────────────────────────

export function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <h3 id="modal-title">{title}</h3>
        {children}
      </div>
    </div>
  );
}

/** A failed write: what happened, the request ID, and a reload when it was stale. */
export function WriteFailureNotice({ failure, onReload }: { failure: WriteFailure; onReload?: () => void }) {
  return (
    <div className="error" role="alert">
      <span>{failure.message}</span>
      <span className="ref">{failure.reference}</span>
      {failure.stale && onReload ? (
        <button className="btn" type="button" onClick={onReload}>
          {t("team.reload")}
        </button>
      ) : null}
    </div>
  );
}

export function ReasonField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <div className="field">
      <label htmlFor="team-reason">{t("team.reason")}</label>
      <textarea id="team-reason" rows={2} value={value} onChange={(event) => onChange(event.target.value)} />
      <span className="meta">{t("team.reasonHint")}</span>
    </div>
  );
}

/**
 * One write intent: the same Idempotency-Key until the person changes what
 * they ask for (any field edit) or it succeeds. A failure keeps the key, so
 * "try again" after a lost answer can't do it twice.
 */
export function useIntentWrite() {
  const intent = useRef(createIntent());
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [problem, setProblem] = useState<string | null>(null);

  async function run<T>(check: string | null, send: (key: string) => Promise<T>, done: (value: T) => void) {
    setProblem(check);
    if (check) return;
    setPending(true);
    setError(null);
    try {
      const value = await send(intent.current.key());
      intent.current.reset();
      done(value);
    } catch (failure) {
      setError(failure);
    } finally {
      setPending(false);
    }
  }

  /** A field changed: a new intent, and the old error no longer applies. */
  const edited = () => {
    intent.current.reset();
    setProblem(null);
  };

  return { pending, error, problem, run, edited };
}

export type ReasonDialogViewProps = {
  title: string;
  body: ReactNode;
  reason: string;
  onReason: (reason: string) => void;
  submitLabel: string;
  danger?: boolean;
  pending: boolean;
  problem: string | null;
  error: unknown;
  onSubmit: () => void;
  onClose: () => void;
  onReload?: () => void;
  children?: ReactNode;
};

/** The shape every team write takes: what it does, extra fields, a reason, send. */
export function ReasonDialogView(props: ReasonDialogViewProps) {
  const submit = (event: FormEvent) => {
    event.preventDefault();
    props.onSubmit();
  };
  return (
    <Modal title={props.title} onClose={props.onClose}>
      <form className="modal-form" onSubmit={submit} noValidate>
        <div className="modal-body">{props.body}</div>
        {props.children}
        <ReasonField value={props.reason} onChange={props.onReason} />
        {props.problem ? <p className="field-problem">{props.problem}</p> : null}
        {props.error ? (
          <WriteFailureNotice failure={describeWriteFailure(props.error)} onReload={props.onReload} />
        ) : null}
        <div className="btns modal-actions">
          <button className="btn quiet" type="button" onClick={props.onClose}>
            {t("team.cancel")}
          </button>
          <button className={`btn ${props.danger ? "danger" : "primary"}`} type="submit" disabled={props.pending}>
            {props.pending ? t("team.sending") : props.submitLabel}
          </button>
        </div>
      </form>
    </Modal>
  );
}

type Common = { onClose: () => void; onReload: () => void; onDone: (notice: string) => void };

// ── Grid: grant or revoke a feature on a role ─────────────────────────────

export function FeatureDialog(
  props: Common & {
    role: StaffRoleListItem;
    feature: GridFeature;
    enabled: boolean;
    onRole: (role: StaffRoleListItem) => void;
  },
) {
  const { client } = useApi();
  const write = useIntentWrite();
  const [reason, setReason] = useState("");
  const name = featureName(props.feature.settingId);
  const values = {
    feature: name,
    role: props.role.name,
    members: countLabel(props.role.memberCount, "team.grid.membersOne", "team.grid.members"),
  };
  return (
    <ReasonDialogView
      title={t(props.enabled ? "team.grant.title" : "team.revoke.title")}
      body={
        <>
          <p>{t(props.enabled ? "team.grant.body" : "team.revoke.body", values)}</p>
          <p className="mono meta">{props.feature.settingId}</p>
          {props.feature.sensitivity === "critical" ? <p className="meta">{t("team.critical.note")}</p> : null}
          <PermissionsGoToo revoking={!props.enabled} role={props.role} feature={props.feature} />
        </>
      }
      reason={reason}
      onReason={(next) => {
        write.edited();
        setReason(next);
      }}
      submitLabel={t(props.enabled ? "team.grant.submit" : "team.revoke.submit")}
      danger={!props.enabled}
      pending={write.pending}
      problem={write.problem}
      error={write.error}
      onClose={props.onClose}
      onReload={props.onReload}
      onSubmit={() =>
        void write.run(
          reasonProblem(reason),
          (key) =>
            sendFeatureChange(
              client,
              { role: props.role, settingId: props.feature.settingId, enabled: props.enabled, reason },
              key,
            ),
          ({ role }) => {
            props.onRole(role);
            props.onDone(t(props.enabled ? "team.grant.done" : "team.revoke.done", { feature: name, role: role.name }));
          },
        )
      }
    />
  );
}

/** Revoking a feature revokes the permissions the role holds under it: name them. */
function PermissionsGoToo({
  revoking,
  role,
  feature,
}: {
  revoking: boolean;
  role: StaffRoleListItem;
  feature: GridFeature;
}) {
  if (!revoking) return null;
  const under = permissionRows(feature.settingId).filter((permission) => rolePermission(role, permission.settingId));
  if (under.length === 0) return null;
  const names = under.map((permission) => permissionName(permission.settingId)).join(" · ");
  return <p className="meta">{t("team.revoke.permissionsToo", { permissions: names })}</p>;
}

// ── Grid: a permission under a feature (ADR-0019) ────────────────────────

/** What was typed that can't be sent yet. */
function FieldProblem({ problem }: { problem: string | null }) {
  if (!problem) return null;
  return <p className="field-problem">{problem}</p>;
}

function WriteError({ error, onReload }: { error: unknown; onReload?: () => void }) {
  if (!error) return null;
  return <WriteFailureNotice failure={describeWriteFailure(error)} onReload={onReload} />;
}

/** Only offered on a permission the role already holds. */
function RevokeButton({ granted, pending, onRevoke }: { granted: boolean; pending: boolean; onRevoke: () => void }) {
  if (!granted) return null;
  return (
    <button className="btn danger" type="button" disabled={pending} onClick={onRevoke}>
      {t("team.revoke.submit")}
    </button>
  );
}

/** The limits a permission is granted with: one row per currency, none for no ceiling. */
function LimitsField(props: {
  /** A permission that carries no limit has none to edit. */
  limited: boolean;
  rows: readonly LimitRow[];
  suggestions: readonly string[];
  newCurrency: string;
  onRows: (rows: LimitRow[]) => void;
}) {
  const { rows, onRows } = props;
  if (!props.limited) return null;
  const change = (index: number, patch: Partial<LimitRow>) =>
    onRows(rows.map((row, at) => ({ ...row, ...(at === index ? patch : {}) })));
  return (
    <div className="field limits-field">
      <span className="field-label" id="limits-label">
        {t("team.permission.limits")}
      </span>
      <LimitsHint empty={rows.length === 0} />
      <ul className="limit-rows" aria-labelledby="limits-label">
        {rows.map((row, index) => (
          <li key={index}>
            <label className="visually-hidden" htmlFor={`limit-currency-${index}`}>
              {t("team.permission.currency")}
            </label>
            <input
              id={`limit-currency-${index}`}
              className="limit-currency"
              list="limit-currencies"
              maxLength={3}
              autoComplete="off"
              placeholder={t("team.permission.currency")}
              value={row.currency}
              onChange={(event) => change(index, { currency: event.target.value.toUpperCase() })}
            />
            <label className="visually-hidden" htmlFor={`limit-amount-${index}`}>
              {t("team.permission.amount")}
            </label>
            <input
              id={`limit-amount-${index}`}
              className="limit-amount"
              type="number"
              min="1"
              inputMode="numeric"
              placeholder={t("team.permission.amount")}
              value={row.amount}
              onChange={(event) => change(index, { amount: event.target.value })}
            />
            <button
              className="btn small danger"
              type="button"
              aria-label={t("team.permission.removeLimit", {
                currency: row.currency,
              })}
              onClick={() => onRows(rows.filter((_, at) => at !== index))}
            >
              ✕
            </button>
          </li>
        ))}
      </ul>
      <div className="btns">
        <button
          className="btn small"
          type="button"
          onClick={() => onRows([...rows, { currency: props.newCurrency, amount: "" }])}
        >
          {t("team.permission.addLimit")}
        </button>
      </div>
      <datalist id="limit-currencies">
        {props.suggestions.map((currency) => (
          <option key={currency} value={currency} />
        ))}
      </datalist>
    </div>
  );
}

function LimitsHint({ empty }: { empty: boolean }) {
  if (empty) return <span className="meta">{t("team.permission.noLimitHint")}</span>;
  return <span className="meta">{t("team.permission.limitsHint")}</span>;
}

/** What happens to someone who holds the feature but not this permission. */
function WithoutIt({ approvable }: { approvable: boolean }) {
  if (approvable) return <p className="meta">{t("team.permission.approvable")}</p>;
  return <p className="meta">{t("team.permission.refusable")}</p>;
}

/**
 * Grants a permission to a role, changes its limits, or revokes it. The
 * server checks who may grant it, and that the role already grants the
 * feature underneath; its refusal is shown with the request ID.
 */
export function PermissionDialog(
  props: Common & {
    me: GetStaffMeResponse;
    role: StaffRoleListItem;
    permission: GridPermission;
    onRole: (role: StaffRoleListItem) => void;
  },
) {
  const { client } = useApi();
  const write = useIntentWrite();
  const { permission, role } = props;
  const granted = rolePermission(role, permission.settingId);
  const [reason, setReason] = useState("");
  // A fresh grant starts from the granter's own ceilings: starting empty means
  // "no ceiling", which the server refuses to anyone who has one (ADR-0019 §4.1).
  const mine = heldPermission(props.me, permission.settingId)?.limits ?? [];
  const [rows, setRows] = useState<LimitRow[]>(limitRowsOf(granted?.limits ?? mine));
  // Grant and revoke are one action, so the server's replay of a repeated key
  // is blind to which one it stood for: a change of direction is a new intent.
  const lastEnabled = useRef<boolean | null>(null);
  const name = permissionName(permission.settingId);
  const values = {
    permission: name,
    role: role.name,
    members: countLabel(role.memberCount, "team.grid.membersOne", "team.grid.members"),
  };

  const send = (enabled: boolean) => {
    if (lastEnabled.current !== null && lastEnabled.current !== enabled) write.edited();
    lastEnabled.current = enabled;
    const limits = () => {
      if (!enabled) return undefined;
      if (!permission.limited) return undefined;
      return limitsOfRows(rows);
    };
    const problem = () => {
      if (!enabled) return reasonProblem(reason);
      return limitsProblem(rows) ?? reasonProblem(reason);
    };
    void write.run(
      problem(),
      (key) =>
        sendPermissionChange(
          client,
          {
            role,
            settingId: permission.settingId,
            enabled,
            limits: limits(),
            reason,
          },
          key,
        ),
      ({ role: saved }) => {
        props.onRole(saved);
        const done = enabled ? "team.permission.grantDone" : "team.permission.revokeDone";
        props.onDone(t(done, { permission: name, role: saved.name }));
      },
    );
  };

  let title = t("team.permission.grantTitle");
  let body = <p>{t("team.permission.grantBody", values)}</p>;
  let submitLabel = t("team.grant.submit");
  if (granted) {
    title = t("team.permission.editTitle");
    body = <p>{t("team.permission.editBody", values)}</p>;
    submitLabel = t("team.role.save");
  }
  if (write.pending) submitLabel = t("team.sending");

  return (
    <Modal title={title} onClose={props.onClose}>
      <form
        className="modal-form"
        onSubmit={(event) => {
          event.preventDefault();
          send(true);
        }}
        noValidate
      >
        <div className="modal-body">
          {body}
          <p className="mono meta">{permission.settingId}</p>
          <p className="meta">
            {t("team.permission.under", {
              feature: featureName(permission.feature),
            })}
          </p>
          <WithoutIt approvable={permission.approvable} />
        </div>
        <LimitsField
          limited={permission.limited}
          rows={rows}
          suggestions={currencySuggestions(role, props.me)}
          newCurrency={defaultCurrency(role)}
          onRows={(next) => {
            write.edited();
            setRows(next);
          }}
        />
        <ReasonField
          value={reason}
          onChange={(next) => {
            write.edited();
            setReason(next);
          }}
        />
        <FieldProblem problem={write.problem} />
        <WriteError error={write.error} onReload={props.onReload} />
        <div className="btns modal-actions">
          <button className="btn quiet" type="button" onClick={props.onClose}>
            {t("team.cancel")}
          </button>
          <RevokeButton granted={granted !== null} pending={write.pending} onRevoke={() => send(false)} />
          <button className="btn primary" type="submit" disabled={write.pending}>
            {submitLabel}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── A member: deactivate, end sessions ───────────────────────────────────

export function DeactivateDialog(
  props: Common & { member: StaffMemberListItem; onMember: (member: StaffMemberListItem) => void },
) {
  const { client } = useApi();
  const write = useIntentWrite();
  const [reason, setReason] = useState("");
  const { name } = props.member;
  return (
    <ReasonDialogView
      title={t("team.deactivate.title", { name })}
      body={<p>{t("team.deactivate.body", { name })}</p>}
      reason={reason}
      onReason={(next) => {
        write.edited();
        setReason(next);
      }}
      submitLabel={t("team.deactivate.submit")}
      danger
      pending={write.pending}
      problem={write.problem}
      error={write.error}
      onClose={props.onClose}
      onReload={props.onReload}
      onSubmit={() =>
        void write.run(
          reasonProblem(reason),
          (key) => sendDeactivate(client, props.member, reason, key),
          ({ staffMember, endedSessions }) => {
            props.onMember(staffMember);
            props.onDone(t("team.deactivate.done", { name, count: endedSessions }));
          },
        )
      }
    />
  );
}

/**
 * Put someone back (WALLIYO-0121). Not a danger dialog: deactivating was the
 * irreversible one, and this is the way out of it.
 */
export function ReactivateDialog(
  props: Common & { member: StaffMemberListItem; onMember: (member: StaffMemberListItem) => void },
) {
  const { client } = useApi();
  const write = useIntentWrite();
  const [reason, setReason] = useState("");
  const { name } = props.member;
  return (
    <ReasonDialogView
      title={t("team.reactivate.title", { name })}
      body={<p>{t("team.reactivate.body", { name })}</p>}
      reason={reason}
      onReason={(next) => {
        write.edited();
        setReason(next);
      }}
      submitLabel={t("team.reactivate.submit")}
      pending={write.pending}
      problem={write.problem}
      error={write.error}
      onClose={props.onClose}
      onReload={props.onReload}
      onSubmit={() =>
        void write.run(
          reasonProblem(reason),
          (key) => sendReactivate(client, props.member, reason, key),
          ({ staffMember }) => {
            props.onMember(staffMember);
            props.onDone(t("team.reactivate.done", { name }));
          },
        )
      }
    />
  );
}

export function EndSessionsDialog(props: Common & { member: StaffMemberListItem; self: boolean; onEnded: () => void }) {
  const { client } = useApi();
  const write = useIntentWrite();
  const [reason, setReason] = useState("");
  const { name, openSessions } = props.member;
  return (
    <ReasonDialogView
      title={t("team.endSessions.title", { name })}
      body={
        <>
          <p>
            {t("team.endSessions.body", {
              name,
              sessions: countLabel(openSessions, "team.sessionsOne", "team.sessions"),
            })}
          </p>
          {props.self ? <p className="meta">{t("team.endSessions.self")}</p> : null}
        </>
      }
      reason={reason}
      onReason={(next) => {
        write.edited();
        setReason(next);
      }}
      submitLabel={t("team.endSessions.submit")}
      danger
      pending={write.pending}
      problem={write.problem}
      error={write.error}
      onClose={props.onClose}
      onReload={props.onReload}
      onSubmit={() =>
        void write.run(
          reasonProblem(reason),
          (key) => sendEndSessions(client, props.member, reason, key),
          ({ endedSessions }) => {
            props.onEnded();
            props.onDone(t("team.endSessions.done", { name, count: endedSessions }));
          },
        )
      }
    />
  );
}

// ── Invite ───────────────────────────────────────────────────────────────

export function InviteDialog(
  props: Common & { roles: readonly StaffRoleListItem[]; onMember: (member: StaffMemberListItem) => void },
) {
  const { client } = useApi();
  const write = useIntentWrite();
  const [reason, setReason] = useState("");
  const [fields, setFields] = useState<InviteFields>({ name: "", email: "", phone: "", roleIds: [] });
  const edit = (patch: Partial<InviteFields>) => {
    write.edited();
    setFields((current) => ({ ...current, ...patch }));
  };
  return (
    <ReasonDialogView
      title={t("team.invite.title")}
      body={<p className="meta">{t("team.invite.sms", { hours: SET_PASSWORD_CODE_TTL_HOURS })}</p>}
      reason={reason}
      onReason={(next) => {
        write.edited();
        setReason(next);
      }}
      submitLabel={t("team.invite.submit")}
      pending={write.pending}
      problem={write.problem}
      error={write.error}
      onClose={props.onClose}
      onReload={props.onReload}
      onSubmit={() =>
        void write.run(
          inviteProblem(fields) ?? reasonProblem(reason),
          (key) => sendInvite(client, fields, reason, key),
          ({ staffMember, setPasswordCodeExpiresAt }) => {
            props.onMember(staffMember);
            props.onDone(
              t("team.invite.done", { name: staffMember.name, until: formatDateTime(setPasswordCodeExpiresAt) }),
            );
          },
        )
      }
    >
      <div className="field">
        <label htmlFor="invite-name">{t("team.invite.name")}</label>
        <input
          id="invite-name"
          autoComplete="off"
          value={fields.name}
          onChange={(event) => edit({ name: event.target.value })}
        />
      </div>
      <div className="field">
        <label htmlFor="invite-email">{t("team.invite.email")}</label>
        <input
          id="invite-email"
          type="email"
          autoComplete="off"
          value={fields.email}
          onChange={(event) => edit({ email: event.target.value })}
        />
      </div>
      <div className="field">
        <label htmlFor="invite-phone">{t("team.invite.phone")}</label>
        <input
          id="invite-phone"
          type="tel"
          inputMode="tel"
          placeholder="+224621234567"
          value={fields.phone}
          onChange={(event) => edit({ phone: event.target.value })}
        />
        <span className="meta">{t("team.invite.phoneHint")}</span>
      </div>
      {/* Their roles, and through them everything they may do. Picked here
          rather than in a second step, because someone invited with none signs
          in to a console with no screens open to them (WALLIYO-0120). The
          server checks each one the way assigning it separately would be. */}
      <fieldset className="field">
        <legend>{t("team.invite.roles")}</legend>
        {props.roles.length === 0 ? (
          <span className="meta">{t("team.invite.noRoles")}</span>
        ) : (
          props.roles.map((role) => (
            <label key={role.staffRoleId} className="check">
              <input
                type="checkbox"
                checked={fields.roleIds.includes(role.staffRoleId)}
                onChange={(event) =>
                  edit({
                    roleIds: event.target.checked
                      ? [...fields.roleIds, role.staffRoleId]
                      : fields.roleIds.filter((id) => id !== role.staffRoleId),
                  })
                }
              />
              <span>{role.name}</span>
            </label>
          ))
        )}
        {fields.roleIds.length === 0 && props.roles.length > 0 && (
          <span className="meta">{t("team.invite.rolesHint")}</span>
        )}
      </fieldset>
    </ReasonDialogView>
  );
}

// ── Roles: create, rename or describe ────────────────────────────────────

export function RoleDialog(
  props: Common & { role: StaffRoleListItem | null; onRole: (role: StaffRoleListItem) => void },
) {
  const { client } = useApi();
  const write = useIntentWrite();
  const { role } = props;
  const [reason, setReason] = useState("");
  const [fields, setFields] = useState<RoleFields>({
    name: role?.name ?? "",
    description: role?.description ?? "",
    department: role?.department ?? "",
  });
  const edit = (patch: Partial<RoleFields>) => {
    write.edited();
    setFields((current) => ({ ...current, ...patch }));
  };
  return (
    <ReasonDialogView
      title={role ? t("team.role.editTitle", { name: role.name }) : t("team.role.createTitle")}
      body={role ? <p className="meta">{t("team.role.version", { version: role.version })}</p> : null}
      reason={reason}
      onReason={(next) => {
        write.edited();
        setReason(next);
      }}
      submitLabel={role ? t("team.role.save") : t("team.role.create")}
      pending={write.pending}
      problem={write.problem}
      error={write.error}
      onClose={props.onClose}
      onReload={props.onReload}
      onSubmit={() =>
        void write.run(
          roleFieldsProblem(fields, role ?? undefined) ?? reasonProblem(reason),
          (key) =>
            role ? sendEditRole(client, role, fields, reason, key) : sendCreateRole(client, fields, reason, key),
          ({ role: saved }) => {
            props.onRole(saved);
            props.onDone(t(role ? "team.role.saved" : "team.role.created", { name: saved.name }));
          },
        )
      }
    >
      <div className="field">
        <label htmlFor="role-name">{t("team.role.name")}</label>
        <input id="role-name" value={fields.name} onChange={(event) => edit({ name: event.target.value })} />
      </div>
      <div className="field">
        <label htmlFor="role-description">{t("team.role.description")}</label>
        <textarea
          id="role-description"
          rows={2}
          value={fields.description}
          onChange={(event) => edit({ description: event.target.value })}
        />
        <span className="meta">{t("team.role.descriptionHint")}</span>
      </div>
      {/* Which workspace its holders land in (WALLIYO-0132). Not an access
          scope: what they may do is still the features below. Departments
          with nothing to open are offered anyway, because a role can belong
          to Legal before Legal has a screen. */}
      <div className="field">
        <label htmlFor="role-department">{t("team.role.department")}</label>
        <select
          id="role-department"
          value={fields.department}
          onChange={(event) => edit({ department: event.target.value as RoleFields["department"] })}
        >
          <option value="">{t("team.role.noDepartment")}</option>
          {DEPARTMENT_IDS.map((id) => (
            <option key={id} value={id}>
              {workspaceLabel(id)}
            </option>
          ))}
        </select>
        <span className="meta">{t("team.role.departmentHint")}</span>
      </div>
    </ReasonDialogView>
  );
}

// ── A member's roles: assign or remove ───────────────────────────────────

export function MembershipDialog(
  props: Common & {
    member: StaffMemberListItem;
    roles: readonly StaffRoleListItem[];
    onMember: (member: StaffMemberListItem) => void;
  },
) {
  const { client } = useApi();
  const [reason, setReason] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  // One intent per (role, direction), kept across a retry, dropped when the reason changes.
  const intents = useRef(new Map<string, ReturnType<typeof createIntent>>());
  const { member } = props;
  const held = new Set(member.roles.map((role) => role.staffRoleId));

  async function change(role: StaffRoleListItem, assign: boolean) {
    const check = reasonProblem(reason);
    setProblem(check ? t("team.roles.reasonFirst") : null);
    if (check) return;
    const id = `${assign ? "assign" : "remove"}:${role.staffRoleId}`;
    const intent = intents.current.get(id) ?? createIntent();
    intents.current.set(id, intent);
    setPending(id);
    setError(null);
    setDone(null);
    try {
      const { staffMember } = await sendMembership(client, { member, role, assign, reason }, intent.key());
      intents.current.delete(id);
      props.onMember(staffMember);
      const message = t(assign ? "team.roles.assignDone" : "team.roles.removeDone", {
        role: role.name,
        name: member.name,
      });
      setDone(message);
      props.onDone(message);
    } catch (failure) {
      setError(failure);
    } finally {
      setPending(null);
    }
  }

  const button = (role: StaffRoleListItem, assign: boolean) => {
    const id = `${assign ? "assign" : "remove"}:${role.staffRoleId}`;
    return (
      <li key={role.staffRoleId}>
        <span className="chip">{role.name}</span>
        <button
          className={`btn small ${assign ? "" : "danger"}`}
          type="button"
          disabled={pending !== null}
          aria-label={t(assign ? "team.roles.assign" : "team.roles.remove", { role: role.name })}
          onClick={() => void change(role, assign)}
        >
          {pending === id ? t("team.sending") : t(assign ? "team.grant.submit" : "team.revoke.submit")}
        </button>
      </li>
    );
  };

  const heldRoles = props.roles.filter((role) => held.has(role.staffRoleId));
  const otherRoles = props.roles.filter((role) => !held.has(role.staffRoleId));
  return (
    <Modal title={t("team.roles.title", { name: member.name })} onClose={props.onClose}>
      <div className="modal-form">
        <ReasonField
          value={reason}
          onChange={(next) => {
            intents.current.clear();
            setProblem(null);
            setReason(next);
          }}
        />
        {problem ? <p className="field-problem">{problem}</p> : null}
        <div className="role-lists">
          <div>
            <h4>{t("team.roles.held")}</h4>
            {heldRoles.length ? (
              <ul className="role-list">{heldRoles.map((role) => button(role, false))}</ul>
            ) : (
              <p className="meta">{t("team.roles.none")}</p>
            )}
          </div>
          <div>
            <h4>{t("team.roles.available")}</h4>
            {otherRoles.length ? (
              <ul className="role-list">{otherRoles.map((role) => button(role, true))}</ul>
            ) : (
              <p className="meta">{t("team.roles.none")}</p>
            )}
          </div>
        </div>
        {error ? <WriteFailureNotice failure={describeWriteFailure(error)} onReload={props.onReload} /> : null}
        {done && !error ? (
          <p className="banner ok" role="status">
            {done}
          </p>
        ) : null}
        <div className="btns modal-actions">
          <button className="btn" type="button" onClick={props.onClose}>
            {t("team.close")}
          </button>
        </div>
      </div>
    </Modal>
  );
}
